import React, { useMemo, useState } from 'react';
import { MetricTile } from '../components/MetricTile';
import { StatusBarChart } from '../components/StatusBarChart';
import { PipelineDrilldownModal } from '../components/PipelineDrilldownModal';
import { activeStatuses, formatVolume, pipelineRecords, potentialStatuses, sumVolume, withinDays, type PipelineRecord } from '../data/pipeline';
type DateColumn = {
  label: string;
  get: (record: PipelineRecord) => number | undefined;
};
const appTakenColumn: DateColumn = {
  label: 'App taken',
  get: record => record.appTakenDaysAgo
};
const docsOutColumn: DateColumn = {
  label: 'Docs out',
  get: record => record.docsOutDaysAgo
};
const submittedColumn: DateColumn = {
  label: 'Submitted',
  get: record => record.submittedDaysAgo
};
type Drilldown = {
  title: string;
  records: PipelineRecord[];
  dateColumn: DateColumn;
};
const purposeOptions = ['All', 'Purchase', 'Refinance'] as const;
const assistantOptions = ['All', 'Handy N.', 'Renee P.'] as const;
export function Reports() {
  const [purpose, setPurpose] = useState<(typeof purposeOptions)[number]>('All');
  const [assistant, setAssistant] = useState<(typeof assistantOptions)[number]>('All');
  const [drilldown, setDrilldown] = useState<Drilldown | null>(null);
  const records = useMemo(() => pipelineRecords.filter(record => {
    if (purpose !== 'All' && record.purpose !== purpose) return false;
    if (assistant !== 'All' && record.assistant !== assistant) return false;
    return true;
  }), [purpose, assistant]);
  const active = records.filter(record => record.bucket === 'active');
  const potential = records.filter(record => record.bucket === 'potential');
  const appTakenWeek = records.filter(record => withinDays(record.appTakenDaysAgo, 7));
  const appTaken30 = records.filter(record => withinDays(record.appTakenDaysAgo, 30));
  const docsOutWeek = records.filter(record => withinDays(record.docsOutDaysAgo, 7));
  const docsOut30 = records.filter(record => withinDays(record.docsOutDaysAgo, 30));
  const submittedWeek = records.filter(record => withinDays(record.submittedDaysAgo, 7));
  const submitted30 = records.filter(record => withinDays(record.submittedDaysAgo, 30));
  const barsFor = (source: PipelineRecord[], statuses: readonly string[]) => statuses.map(status => {
    const matching = source.filter(record => record.status === status);
    return {
      status,
      count: matching.length,
      volume: matching.length > 0 ? formatVolume(sumVolume(matching)) : '—'
    };
  });
  const openStatus = (source: PipelineRecord[], status: string, pipelineLabel: string) => setDrilldown({
    title: `${pipelineLabel} · ${status}`,
    records: source.filter(record => record.status === status),
    dateColumn: appTakenColumn
  });
  const selectClass = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px] text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20';
  return <div className="space-y-4" data-id="element-944" data-mp="5m7bafi7jkqh">
      <div data-id="element-945" data-mp="pz01d74sbol">
        <h1 className="text-[22px] font-bold text-ink" data-id="element-946" data-mp="qiak8ugbxsl">Reports</h1>
        <p className="mt-1 text-[13px] text-gray-500" data-id="element-947" data-mp="fe3bpx7rnmxv">
          Your pipeline · every tile drills into the records behind it
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4 sm:grid-cols-2 lg:max-w-2xl" data-id="element-948" data-mp="am244oljmke6">
        <div data-id="element-949" data-mp="ogw3e1kv20o8">
          <label htmlFor="filter-purpose" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-950" data-mp="kdg6d44vpyb0">
            Loan purpose
          </label>
          <select id="filter-purpose" value={purpose} onChange={event => setPurpose(event.target.value as (typeof purposeOptions)[number])} className={selectClass} data-id="element-951" data-mp="tdmj2nsr6u94">
            {purposeOptions.map(option => <option key={option} value={option} data-id="element-952" data-mp="2wn6bcok3x71v">
                {option}
              </option>)}
          </select>
        </div>
        <div data-id="element-953" data-mp="31k6lbak2uty9">
          <label htmlFor="filter-assistant" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-954" data-mp="31nubq0u8708r">
            Loan officer assistant
          </label>
          <select id="filter-assistant" value={assistant} onChange={event => setAssistant(event.target.value as (typeof assistantOptions)[number])} className={selectClass} data-id="element-955" data-mp="2dw6pvxbtmfdc">
            {assistantOptions.map(option => <option key={option} value={option} data-id="element-956" data-mp="2sll35giipgy0">
                {option}
              </option>)}
          </select>
        </div>
      </div>

      <section aria-label="Active pipeline" className="space-y-3" data-id="element-957" data-mp="1v5fekq89wewb">
        <h2 className="text-[13px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-958" data-mp="203qn8xmu0jcy">
          Active pipeline
        </h2>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[240px_minmax(0,1fr)_240px]" data-id="element-959" data-mp="h8uqzk29ez7v">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1" data-id="element-960" data-mp="d8kf5qrsdel3">
            <MetricTile label="Active pipeline units" value={String(active.length)} caption="loans working toward closing" onOpen={() => setDrilldown({
            title: 'Active pipeline units',
            records: active,
            dateColumn: appTakenColumn
          })} data-id="element-961" data-mp="87o854c51cbg" />
            <MetricTile label="Active pipeline volume" value={formatVolume(sumVolume(active))} caption="total loan amount in play" tone="volume" onOpen={() => setDrilldown({
            title: 'Active pipeline volume',
            records: active,
            dateColumn: appTakenColumn
          })} data-id="element-962" data-mp="3lonl9s392v7b" />
          </div>

          <StatusBarChart title="Active pipeline by status" bars={barsFor(active, activeStatuses)} onSelect={status => openStatus(active, status, 'Active pipeline')} data-id="element-963" data-mp="321ss64j64lg7" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1" data-id="element-964" data-mp="2s34lg1wmvsrt">
            <MetricTile label="Potential pipeline units" value={String(potential.length)} caption="applications not yet in process" onOpen={() => setDrilldown({
            title: 'Potential pipeline units',
            records: potential,
            dateColumn: appTakenColumn
          })} data-id="element-965" data-mp="3ftqv1xmadl3x" />
            <MetricTile label="Potential pipeline volume" value={formatVolume(sumVolume(potential))} caption="upside if all convert" tone="volume" onOpen={() => setDrilldown({
            title: 'Potential pipeline volume',
            records: potential,
            dateColumn: appTakenColumn
          })} data-id="element-966" data-mp="2mozo77uvw2b" />
          </div>
        </div>

        <StatusBarChart title="Potential pipeline by status" bars={barsFor(potential, potentialStatuses)} onSelect={status => openStatus(potential, status, 'Potential pipeline')} data-id="element-967" data-mp="1wv2oazhnssj8" />
      </section>

      <section aria-label="Production" className="space-y-3" data-id="element-968" data-mp="147f5npzy8bz8">
        <h2 className="text-[13px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-969" data-mp="1j34ndby3q2y5">
          Production
        </h2>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-970" data-mp="1e39tczj14x8v">
          <MetricTile label="App taken this week" value={String(appTakenWeek.length)} caption={formatVolume(sumVolume(appTakenWeek))} onOpen={() => setDrilldown({
          title: 'Apps taken this week',
          records: appTakenWeek,
          dateColumn: appTakenColumn
        })} data-id="element-971" data-mp="2uijj6cekqoro" />
          <MetricTile label="Docs out this week" value={String(docsOutWeek.length)} caption={formatVolume(sumVolume(docsOutWeek))} onOpen={() => setDrilldown({
          title: 'Docs out this week',
          records: docsOutWeek,
          dateColumn: docsOutColumn
        })} data-id="element-972" data-mp="wuz2fboby93q" />
          <MetricTile label="Submissions this week" value={String(submittedWeek.length)} caption={formatVolume(sumVolume(submittedWeek))} onOpen={() => setDrilldown({
          title: 'Submissions this week',
          records: submittedWeek,
          dateColumn: submittedColumn
        })} data-id="element-973" data-mp="1flgvjhd2r6pm" />
          <MetricTile label="App taken last 30 days" value={String(appTaken30.length)} caption={formatVolume(sumVolume(appTaken30))} onOpen={() => setDrilldown({
          title: 'Apps taken in the last 30 days',
          records: appTaken30,
          dateColumn: appTakenColumn
        })} data-id="element-974" data-mp="3esf6d55aombz" />
          <MetricTile label="Docs out last 30 days" value={String(docsOut30.length)} caption={formatVolume(sumVolume(docsOut30))} onOpen={() => setDrilldown({
          title: 'Docs out in the last 30 days',
          records: docsOut30,
          dateColumn: docsOutColumn
        })} data-id="element-975" data-mp="1lltbm5rausy" />
          <MetricTile label="Submissions last 30 days" value={String(submitted30.length)} caption={formatVolume(sumVolume(submitted30))} onOpen={() => setDrilldown({
          title: 'Submissions in the last 30 days',
          records: submitted30,
          dateColumn: submittedColumn
        })} data-id="element-976" data-mp="2cdooz0i4o2vv" />
        </div>
      </section>

      <PipelineDrilldownModal title={drilldown?.title ?? null} records={drilldown?.records ?? []} dateColumn={drilldown?.dateColumn ?? appTakenColumn} onClose={() => setDrilldown(null)} data-id="element-977" data-mp="2qf3bl1csebql" />
    </div>;
}